import type { Game } from '../core/game';
import type { Affix, MasterDie, Rarity } from '../core/types';
import { AFFIX_BY_ID } from '../data/affixes';
import { RARITY_NAME } from '../data/machines';
import {
  affixValueOf,
  corruptedFactor,
  dieLevelOf,
  isCorruptedDie,
  dieMaterialOf,
  enrichedFactor,
  forgeMarksOf,
  isEnriched,
  levelFactorOf,
  materialView,
} from './abyssApi';
import { materialSetTexts } from './materialsApi';
import { dieSpellsOf, spellTexts } from './spellsApi';
import { dieArt } from './diceArt';
import { icon } from './icons';
import { formatBig } from '../battle/format';

/**
 * Couleurs de rareté — MVP_DESIGN §7 (gris/vert/bleu/violet/or/turquoise),
 * teintes de la maquette pixel v0.6.0. Elles doivent rester identiques aux
 * jetons `--rarity-*` de `src/style.css` : le nom du dé est colorié ici, le
 * cadre de sa carte l'est en CSS via `data-rarity`.
 */
export const RARITY_COLOR: Record<Rarity, string> = {
  common: '#9a9aa8',
  uncommon: '#5fbf4a',
  rare: '#3e7bd6',
  epic: '#a565e6',
  legendary: '#e0b53a',
  prismatic: '#22d3ee',
};

export function rarityName(rarity: Rarity): string {
  return RARITY_NAME[rarity] ?? rarity;
}

/** Or formaté avec séparateur de milliers français (espace fine). */
export function formatGold(n: number): string {
  if (!Number.isFinite(n)) return '—';
  // Au-delà du milliard, le nombre exact ne se lit plus : suffixes (A77).
  if (n >= 1_000_000_000) return formatBig(n);
  return Math.round(n).toLocaleString('fr-FR');
}

/** Pourcentage arrondi, signe optionnel (+X % / X %). */
export function formatPercent(fraction: number, opts: { sign?: boolean } = {}): string {
  const pct = Math.round(fraction * 100);
  const sign = opts.sign && pct >= 0 ? '+' : '';
  return `${sign}${pct} %`;
}

/**
 * Dégâts : un chiffre après la virgule, virgule française (COMMENTS §6).
 * Volontairement non abrégé — le panneau de debug sert à lire des chiffres
 * exacts, contrairement à l'affichage de combat (`src/battle/format.ts`).
 */
export function formatDamage(n: number): string {
  if (!Number.isFinite(n)) return '—';
  // Exact sous le million (le panneau de debug lit des chiffres précis),
  // suffixes au-delà (A77 : des totaux à 38 chiffres rendaient les tuiles illisibles).
  if (n >= 1_000_000) return formatBig(n);
  return n.toFixed(1).replace('.', ',');
}

export function formatSeconds(s: number, decimals = 1): string {
  // Virgule française comme partout ailleurs dans l'interface (COMMENTS §6).
  return `${s.toFixed(decimals).replace('.', ',')} s`;
}

/** Chrono de combat : « m:ss.d » au-delà d'une minute, sinon « ss.d ». */
export function formatTimer(seconds: number): string {
  const clamped = Math.max(0, seconds);
  const totalTenths = Math.floor(clamped * 10 + 1e-6);
  const whole = Math.floor(totalTenths / 10);
  const tenth = totalTenths % 10;
  const mm = Math.floor(whole / 60);
  const ss = whole % 60;
  if (mm > 0) return `${mm}:${String(ss).padStart(2, '0')}.${tenth}`;
  return `${ss}.${tenth}`;
}

/**
 * Chrono de partie : « hh:mm:ss » (COMMENTS v0.6.0 §2 — « un compteur de temps
 * de la partie »). Les heures ne sont jamais tronquées : une partie de 100 h
 * s'écrit « 100:00:00 » plutôt que de repartir à zéro.
 */
export function formatPlayClock(seconds: number): string {
  const total = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  const hh = Math.floor(total / 3600);
  const mm = Math.floor((total % 3600) / 60);
  const ss = total % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

/** Texte d'un affixe : « Lesté +18 % » / « Temporel +1,2 s » / « Avantageux ». */
export function affixText(affix: Affix): string {
  const def = AFFIX_BY_ID[affix.id];
  if (!def) return affix.id;
  if (def.unit === 'percent') return `${def.name} +${Math.round(affix.value * 100)} %`;
  if (def.unit === 'seconds') return `${def.name} +${affix.value.toFixed(1)} s`;
  return def.name;
}

/** Nom court d'un dé : « d4 » / « d6 ». */
export function dieSidesLabel(sides: number): string {
  return `d${sides}`;
}

/** Anime la valeur textuelle d'un élément de `from` à `to` (compte défilant). */
export function animateNumber(el: HTMLElement, from: number, to: number, duration = 350): void {
  const start = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  if (Math.round(from) === Math.round(to)) {
    el.textContent = formatGold(to);
    return;
  }
  const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const step = (): void => {
    const t = Math.min(1, (now() - start) / duration);
    const eased = 1 - Math.pow(1 - t, 2);
    const value = from + (to - from) * eased;
    el.textContent = formatGold(value);
    if (t < 1) requestAnimationFrame(step);
    else el.textContent = formatGold(to);
  };
  requestAnimationFrame(step);
}

/**
 * Texte d'un affixe **tel que ce dé le porte** (A94) : la valeur passe par
 * `affixValueOf` (Enrichi compris) : la **valeur finale** « Lesté +33 % »
 * (A122-1 : plus de suffixe « (×1,5) », le badge « Enrichi ×1,5 » le dit). Un dé
 * ordinaire donne exactement `affixText`. v0.58.0 (A123-4) : avec le *Rat-Ailé*
 * actif, la valeur ×1,2 d'un dé **équipé** (`Game.affixValueOf`).
 */
export function affixTextOf(die: MasterDie, affix: Affix, game?: Game | null): string {
  // v0.58.0 (A123-4) : le *Rat-Ailé* actif relève aussi les dés équipés non Enrichis (×1,2).
  if (!isEnriched(die) && !game) return affixText(affix);
  const def = AFFIX_BY_ID[affix.id];
  if (!def) return affix.id;
  const value = affixValueOf(game, die, affix);
  if (!isEnriched(die) && value === affix.value) return affixText(affix);
  if (def.unit === 'percent') return `${def.name} +${Math.round(value * 100)} %`;
  if (def.unit === 'seconds') return `${def.name} +${value.toFixed(1)} s`;
  return def.name;
}

/**
 * Construit une petite liste HTML des affixes d'un dé (chaîne déjà échappée :
 * texte pur). Les affixes **nommés** des Prismatiques (v0.26.0, A72-14) n'y
 * figurent pas : ils ont leur propre ligne (`namedAffixLine`).
 */
export function affixListText(die: MasterDie, game?: Game | null): string[] {
  return die.affixes.filter((a) => !AFFIX_BY_ID[a.id]?.named).map((a) => affixTextOf(die, a, game));
}

/** Facteur de niveau lisible : « ×1,5 », « ×2,25 », « ×17 », « ×3 325 ». */
export function formatLevelFactor(factor: number): string {
  if (factor >= 1000) return `×${formatGold(factor)}`;
  if (factor >= 10) return `×${formatNumber(factor, 0)}`;
  return formatMult(factor, 2);
}

/** Classe CSS d'un Matériau (`data-material` porte la couleur côté feuille de style). */
function materialPill(id: string): string {
  const view = materialView(id);
  return `<span class="die-card-material" data-role="die-material" data-material="${id}">${view.name}</span>`;
}

/**
 * Avancement des pièces du Matériau d'un dé (A95) : « 3 pièces : actif » ou
 * « 2 / 3 » selon les dés Maîtres équipés. Dit seulement quand la carte connaît
 * la partie (`game`) : sans elle, la pastille reste celle d'avant.
 */
function materialSetBadge(die: MasterDie, game?: Game | null): string {
  const material = dieMaterialOf(die);
  if (!material || !game) return '';
  const texts = materialSetTexts(game, material);
  return `<span class="die-card-set" data-role="die-material-set">${texts.join(' · ')}</span>`;
}

/**
 * Badges propres au mode infini (A94), posés à la suite de la rareté : niveau et
 * facteur de dégâts « Nv 7 · ×17 », pastille de Matériau, « Corrupted » (A98),
 * « Enrichi », « Fusionné ».
 * Chaîne vide pour un dé de campagne (aucun de ces champs).
 */
export function dieInfiniteBadges(die: MasterDie, game?: Game | null): string {
  let html = '';
  const level = dieLevelOf(die);
  if (level > 0) {
    html += `<span class="die-card-level" data-role="die-level">Nv ${level} · ${formatLevelFactor(levelFactorOf(game, die))}</span>`;
  }
  const material = dieMaterialOf(die);
  if (material) html += materialPill(material);
  // A98 : le badge Corrupted se pose à côté du Matériau ; un dé de campagne n'en a pas.
  if (isCorruptedDie(die)) {
    html += `<span class="die-card-corrupted" data-role="die-corrupted" title="Dé des Corrupted GachaDice : se forge, dégâts plats ×${formatGold(corruptedFactor(game))}">Corrupted</span>`;
  }
  if (isEnriched(die)) html += `<span class="die-card-enriched" data-role="die-enriched">Enrichi ${formatMult(enrichedFactor(), 1)}</span>`;
  if (forgeMarksOf(die).fused) html += '<span class="die-card-fused" data-role="die-fused">Fusionné</span>';
  return html;
}

/**
 * Lignes d'affixes de la carte. Les marques de Forge (A94) se posent sur
 * l'affixe concerné : « Relançable » sur celui que la Relance a désigné,
 * « Absolution » sur celui qu'elle a ajouté ; `forge.rerollSlot` et
 * `absolvedSlot` sont des index de `die.affixes`.
 */
function affixItems(die: MasterDie, game?: Game | null): string {
  const marks = forgeMarksOf(die);
  const ic = icon('affix');
  return die.affixes
    .map((a, i) => ({ a, i }))
    .filter(({ a }) => !AFFIX_BY_ID[a.id]?.named)
    .map(({ a, i }) => {
      let mark = '';
      if (marks.rerollSlot === i) {
        mark += '<span class="die-card-mark" data-mark="reroll" title="Seul affixe de ce dé que la Relance peut encore changer">Relançable</span>';
      }
      if (marks.absolvedSlot === i) {
        mark += '<span class="die-card-mark die-card-mark--absolution" data-mark="absolution" title="Ajouté par Absolution : jamais relançable">Absolution</span>';
      }
      return `<li>${ic}<span>${affixTextOf(die, a, game)}</span>${mark}</li>`;
    })
    .join('');
}

/**
 * Affixe **unique** d'un dé Prismatique nommé (Guinsoo, Chef de chantier,
 * ArchiDicer — A72-14) : nom et description, ou `null` pour un dé ordinaire.
 */
export function namedAffixLine(die: MasterDie): { id: string; name: string; description: string } | null {
  for (const a of die.affixes) {
    const def = AFFIX_BY_ID[a.id];
    if (def?.named) return { id: def.id, name: def.name, description: def.description };
  }
  return null;
}

/**
 * Détail de l'affixe *Pipé* (`loaded`, COMMENTS v0.6.0 §1.3) : « Pipé : 6 faces
 * → 12 ». Le cœur pose sur le dé un champ `loaded: { face, replaced[] }` ;
 * l'interface le lit **structurellement** (il n'existe pas tant que l'affixe
 * n'est pas livré) et renvoie `null` quand le dé n'est pas pipé.
 */
export function loadedText(die: unknown): string | null {
  const raw = (die as { loaded?: unknown } | null)?.loaded;
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as { face?: unknown; replaced?: unknown };
  const face = typeof o.face === 'number' && Number.isFinite(o.face) ? o.face : null;
  const count = Array.isArray(o.replaced) ? o.replaced.length : null;
  if (face === null) return null;
  const faces = count === null ? '' : `${count} face${count > 1 ? 's' : ''} → `;
  return `Pipé : ${faces}${face}`;
}

/**
 * Contenu HTML interne d'une carte de dé (vignette, nom, dX, rareté, affixes,
 * sorts). Réutilisé Boutique (révélation) / Dés.
 *
 * COMMENTS v0.4.1 §2 : « L'interface dés affiche désormais le sort débloqué
 * dans son info-bulle » — les sorts portés par le dé (`MasterDie.spells`)
 * apparaissent donc sur la carte elle-même, le détail (condition + effet) étant
 * déplié au tap dans l'onglet Dés.
 *
 * v0.15.0 (refonte F1, lot 2) : carte facettée de la maquette — vignette SVG à
 * gauche (`ui/diceArt.ts`), colonne de texte à droite (`.die-card-main`) ; le
 * nom prend la couleur de rareté par la CSS (`data-rarity` sur la carte), le
 * badge de rareté est le `.f-badge` du lot 1, les puces sont des icônes.
 * `extraHead` se pose à droite du nom (badge « Meilleur » de la révélation).
 *
 * v0.46.0 (A94) : un dé du mode infini porte en plus son niveau « Nv 7 · ×17 »,
 * la pastille de son Matériau, « Enrichi » (valeurs d'affixes × 1,5, via
 * `affixValueOf` quand `game` est donné), « Fusionné » et les marques de Forge
 * sur ses affixes. Un dé de campagne (sans ces champs) s'affiche **exactement**
 * comme avant.
 */
export function dieCardInnerHtml(die: MasterDie, extraHead = '', game?: Game | null): string {
  const affixes = affixListText(die, game);
  const spells = dieSpellsOf(die);
  // v0.7.0 : *Pipé* remplace la moitié des faces — la face choisie est une
  // information de jeu (le dé devient une pièce), pas un simple libellé.
  const loaded = loadedText(die);
  const unique = namedAffixLine(die);
  const spellIc = icon('spell');
  return `
    <div class="die-card-vig">${dieArt(die.sides, die.rarity, { size: 46 })}</div>
    <div class="die-card-main">
      <div class="die-card-head">
        <span class="die-card-name">${die.name}</span>${extraHead}
      </div>
      <p class="die-card-badge"><span class="die-card-sides">${dieSidesLabel(die.sides)}</span><span class="f-badge" data-rarity="${die.rarity}">${rarityName(die.rarity)}</span>${dieInfiniteBadges(die, game)}${materialSetBadge(die, game)}</p>
      ${
        // A89 : sur un d100 la face moyenne (50,5) est une information de jeu.
        (die.sides as number) > 20
          ? `<p class="die-card-expect" data-role="die-expect">Face moyenne ${formatNumber((die.sides + 1) / 2, 1)}</p>`
          : ''
      }
      ${
        affixes.length
          ? `<ul class="die-card-affixes">${affixItems(die, game)}</ul>`
          : '<p class="die-card-affixes die-card-affixes--none">Sans affixe</p>'
      }
      ${loaded ? `<p class="die-card-loaded" data-role="die-loaded">${loaded}</p>` : ''}
      ${
        unique
          ? `<p class="die-card-unique" data-role="die-unique" data-affix="${unique.id}">${icon('spell')}<span><span class="die-card-unique-name">Affixe unique — ${unique.name}</span></span></p>`
          : ''
      }
      ${
        spells.length
          ? `<ul class="die-card-spells">${spells
              .map((s) => {
                // A95 : les textes suivent les Grimoires (`game.spellDescription`).
                const t = spellTexts(game, s);
                return `<li class="die-card-spell${s.rare ? ' die-card-spell--rare' : ''}">${spellIc}<span><span class="die-card-spell-name">${s.name}</span>${
                  t.condition && t.condition !== '—' ? ` <span class="die-card-spell-cond">${t.condition}</span>` : ''
                }${t.description ? ` <span class="die-card-spell-effect">${t.description}</span>` : ''}</span></li>`;
              })
              .join('')}</ul>`
          : ''
      }
    </div>
  `;
}

/**
 * Nombre « lisible » à la française : jusqu'à `decimals` décimales, zéros de
 * fin retirés (1 → « 1 », 1,5 → « 1,5 », 1,8600 → « 1,86 »). Sert aux détails
 * de calcul de l'écran récapitulatif (COMMENTS §1.2.5), où « ×1,00 » partout
 * rendrait la formule illisible.
 */
export function formatNumber(n: number, decimals = 2): string {
  if (!Number.isFinite(n)) return '—';
  if (n >= 1_000_000) return formatBig(n);
  const fixed = n.toFixed(decimals);
  const trimmed = fixed.includes('.') ? fixed.replace(/0+$/, '').replace(/\.$/, '') : fixed;
  return trimmed.replace('.', ',');
}

/** Multiplicateur : « ×1,86 ». */
export function formatMult(n: number, decimals = 2): string {
  return `×${formatNumber(n, decimals)}`;
}

/** Pourcentage avec une décimale : « 12,5 % » (probabilités de sorts). */
export function formatPercent1(fraction: number): string {
  if (!Number.isFinite(fraction)) return '—';
  return `${formatNumber(fraction * 100, 1)} %`;
}
